import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, memo } from 'react';
import { useDrop } from 'react-dnd';

import { cn } from '@air/utils-tailwind';

import { boardDragTypes, type DropData } from '~/components/Shared/Drag/dragTypes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type DNDBoardRootDropProps = ComponentPropsWithoutRef<typeof Primitive.div>;

export const DNDBoardRootDrop = memo(({ className, children, ...restOfProps }: DNDBoardRootDropProps) => {
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: boardDragTypes,
    drop: (): DropData => ({
      type: 'move',
      location: 'sub-nav-rearrange',
    }),
    collect: (monitor) => {
      const item = monitor.getItem();
      /**
       * Checks if the board that is being dropped is at the root level.
       */
      const isNotRootBoard = item?.firstItem?.library?.id || item?.firstItem?.ancestors?.length !== 0;

      return {
        isOver: monitor.isOver(),
        canDrop: canCreateRootBoards && isNotRootBoard,
      };
    },
  });

  return (
    <Primitive.div
      className={cn('relative', className)}
      data-dnd-droppable={canDrop}
      data-dnd-over={isOver}
      ref={drop}
      {...restOfProps}
    >
      {children}
    </Primitive.div>
  );
});

DNDBoardRootDrop.displayName = 'DNDBoardRootDrop';
